import {get} from 'svelte/store';
import {BehaviorSubject, Subject, combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {LINKS} from '@/const/link';
import {fixLink$} from '@/store';
import {numUtil} from '@/helper/num';
import {calcValueBalanceUtils} from '@/helper/calc-value-balance';
import {marginTradeApi} from '@/api/margin-trade';
import {userInfo$} from '@/streams/user';
import {isUtaSpot$} from '@/streams/uta-state';
import {wwData$} from '@/streams/worker-data';
import {fiatCurrency$} from '@/streams/fiat-currency';
import {indexPricesCache$} from '@/streams/price-index';
import {isActivateMarginAccount$, marginCurrencyMap$} from '@/streams/config/margin-trade';
import {UNIT_TYPES} from '@/modules/assets/overview/const';
import type {UMarginAccounts, UMarginAccountStatus, UMarginAssets, UMarginAssetsOverview, MarginAssetsOverview} from '@px/ws-data';
import {currenciesCache$} from './config/currency';
import {spots$} from './config/spot';

const BTC_PRECISION = 8;
const USD_PRECISION = 2;
const MaxRiskLevelEr = 999; // 无借贷或者资产为0: --, 最大值只展示到 999
const BtcValueFactor = 1;
const UsdValueFactor = 1;

const fixLink = get(fixLink$);

export const InterestValuePrecision = 8;
export const HourlyInterestRatePrecision = 6;
export const AnnualInterestRatePrecision = 2;

export const marginTradeNetEquityAsset$ = new BehaviorSubject(initAssets());
export const marginTradeTotalEquityAsset$ = new BehaviorSubject(initAssets());
export const marginTradeTotalDebtAssets$ = new BehaviorSubject(initAssets());
export const marginTradeTotalMarginAssets$ = new BehaviorSubject(initAssets());
export const marginTradeCrossRiskLevel$ = new BehaviorSubject({riskLevelEr: MaxRiskLevelEr, riskLevel: '--'});

export const loadWallets$ = new Subject<void>();

// total assets
export const marginTotalAssets$ = wwData$.pipe(
  filter((o) => o.type === 'margin-assets'),
  map((o: UMarginAssets) => o.data.assets),
  filter(Boolean),
  startWith(initAssets())
);

export const marginAccountsRows$ = wwData$.pipe(
  filter((o) => o.type === 'margin-trade-accounts'),
  map((o: UMarginAccounts) => o.data.accounts),
  filter(Boolean),
  startWith([])
);

export const marginAccountOverview$ = wwData$.pipe(
  filter((o) => o.type === 'margin-assets-overview'),
  map((o: UMarginAssetsOverview) => o.data),
  filter(Boolean),
  startWith(createInitMarginAssetsOverview())
);

/**
 * 是否激活 Margin Trading 交易账户，默认关闭
 * user 信息中也会返回激活信息 marginAccountStatus 1 为激活
 * 当 Margin Trading 账户订阅 mao.subscribe 推送消息，即可认为用户开启（多端时同步账户状态）
 */
wwData$
  .pipe(
    filter((o) => o.type === 'update-margin-account-status'),
    map((o: UMarginAccountStatus) => o.data.isActivateMarginAccount),
    startWith(false),
    distinctUntilChanged()
  )
  .subscribe((o) => isActivateMarginAccount$.next(o));

export const isSupportMarginTrading$ = combineLatest([userInfo$, isUtaSpot$]).pipe(map(([userInfo, isUtaSpot]) => userInfo.isMain || isUtaSpot));

export const marginAccounts$ = combineLatest([marginAccountsRows$, marginCurrencyMap$, currenciesCache$, spots$]).pipe(map(convertAccounts), shareReplay(1));

function convertAccounts([marginAccounts, marginCurrencyMap, currenciesCache, spots]) {
  return marginAccounts.map((item) => {
    const currencyInfo = marginCurrencyMap[item.currency];
    let tradeParams = {};
    if (!currencyInfo && currenciesCache.has(item.currency)) {
      tradeParams = getTrade(currenciesCache.get(item.currency), spots, true);
    } else {
      tradeParams = getTrade(currencyInfo, spots);
    }
    return {...item, ...currencyInfo, ...tradeParams};
  });
}

export function convertAssetFiatMU({money, btc, usdt}, unit) {
  const map = {
    [UNIT_TYPES.LOCAL_CURRENCY]: money,
    [UNIT_TYPES.BTC]: btc,
    [UNIT_TYPES.USDT]: usdt,
  };
  return map[unit] || '-';
}

combineLatest([marginAccountOverview$, indexPricesCache$, fiatCurrency$])
  .pipe(map(convertAccountOverview), filter(Boolean))
  .subscribe(({totalEquity, netEquity, totalDebt, totalMargin, riskLevel}) => {
    marginTradeNetEquityAsset$.next(netEquity);
    marginTradeTotalEquityAsset$.next(totalEquity);
    marginTradeTotalDebtAssets$.next(totalDebt);
    marginTradeTotalMarginAssets$.next(totalMargin);
    marginTradeCrossRiskLevel$.next(riskLevel);
  });

loadWallets$
  .pipe(
    withLatestFrom(isActivateMarginAccount$),
    filter(([_, isActivateMarginAccount]) => !!isActivateMarginAccount),
    switchMap(() => marginTradeApi.getWallets()),
    filter(({error}) => !error),
    take(1)
  )
  .subscribe();

function getTrade({canTrade, currency, status}, spots, isSpot = false): {tradeLink: string; canTrade: Boolean} {
  if (canTrade || isSpot) {
    const item = spots.find(({baseCurrency, quoteCurrency}) => baseCurrency === currency || quoteCurrency === currency);
    if (item) {
      if ((!['USD', 'USDT'].includes(currency) || status !== 'Listed') && item.status === 'Listed') {
        const pathname = isSpot ? LINKS.PHEMEX_SPOT_TRADE : LINKS.PHEMEX_SPOT_MARGIN_TRADE;
        return {tradeLink: fixLink([pathname, '/', item.urlSymbol].join('')), canTrade: true};
      }
    }
  }
  return {tradeLink: '', canTrade: false};
}

function convertAccountOverview([{riskLevelEr, riskLevel, totalEquityEv, totalDebtEv, netEquityEv, maxTransferOutValueEv, totalMarginEv}, indexPricesCache, fiatCurrency]) {
  const btcIndexPrice = indexPricesCache.get('.BTC');
  if (!btcIndexPrice) {
    return null;
  }

  const rate = indexPricesCache.get('.' + fiatCurrency);
  if (!rate) {
    return null;
  }
  const usdtRate = indexPricesCache.get('.USDT');
  if (!usdtRate) {
    return null;
  }
  const {totalEquity, netEquity, totalDebt, totalMargin} = calcTotalAsset(
    {totalEquityUsdEv: totalEquityEv, netEquityUsdEv: netEquityEv, totalDebtUsdEv: totalDebtEv, totalMarginUsdEv: totalMarginEv},
    rate,
    btcIndexPrice,
    usdtRate,
    UsdValueFactor,
    BtcValueFactor
  );

  /**
   * accounts 只包含全仓所有资产，riskLevel都为全仓riskLevel
   */

  return {
    maxTransferOutValueEv,
    totalEquity,
    netEquity,
    totalDebt,
    totalMargin,
    riskLevel: {
      riskLevelEr,
      // 无借贷或者资产为0: --, 最大值只展示到 999
      riskLevel: totalDebt.usdEv <= 0 ? '--' : riskLevel,
    },
  };
}

function calcTotalAsset({totalEquityUsdEv, netEquityUsdEv, totalDebtUsdEv, totalMarginUsdEv}, rate, btcIndexPrice, usdtRate, usdValueFactor, btcValueFactor) {
  const totalEquityBtcEv = calcValueBalanceUtils.getBalanceEv(totalEquityUsdEv, btcIndexPrice.priceEp, usdValueFactor, btcIndexPrice.priceFactor, btcValueFactor);
  const netEquityBtcEv = calcValueBalanceUtils.getBalanceEv(netEquityUsdEv, btcIndexPrice.priceEp, usdValueFactor, btcIndexPrice.priceFactor, btcValueFactor);
  const totalDebtBtcEv = calcValueBalanceUtils.getBalanceEv(totalDebtUsdEv, btcIndexPrice.priceEp, usdValueFactor, btcIndexPrice.priceFactor, btcValueFactor);
  const totalMarginBtcEv = calcValueBalanceUtils.getBalanceEv(totalMarginUsdEv, btcIndexPrice.priceEp, usdValueFactor, btcIndexPrice.priceFactor, btcValueFactor);

  const totalEquityUsdtEv = calcValueBalanceUtils.getBalanceEv(totalEquityUsdEv, usdtRate.priceEp, usdValueFactor, usdtRate.priceFactor, usdValueFactor);
  const netEquityUsdtEv = calcValueBalanceUtils.getBalanceEv(netEquityUsdEv, usdtRate.priceEp, usdValueFactor, usdtRate.priceFactor, usdValueFactor);
  const totalDebtUsdtEv = calcValueBalanceUtils.getBalanceEv(totalDebtUsdEv, usdtRate.priceEp, usdValueFactor, usdtRate.priceFactor, usdValueFactor);
  const totalMarginUsdtEv = calcValueBalanceUtils.getBalanceEv(totalMarginUsdEv, usdtRate.priceEp, usdValueFactor, usdtRate.priceFactor, usdValueFactor);

  return {
    totalEquity: {
      usdEv: totalEquityUsdEv,
      usd: numUtil.delimit(numUtil.floor(totalEquityUsdEv, USD_PRECISION)),
      money: numUtil.delimit(numUtil.floor(calcValueBalanceUtils.getBalanceEv(totalEquityUsdEv, rate.priceEp, usdValueFactor, rate.priceFactor, usdValueFactor), USD_PRECISION)),
      btcEv: totalEquityBtcEv,
      btc: numUtil.delimit(numUtil.floor(totalEquityBtcEv, BTC_PRECISION)),
      usdtEv: totalEquityUsdtEv,
      usdt: numUtil.delimit(numUtil.floor(totalEquityUsdtEv, USD_PRECISION)),
    },
    netEquity: {
      usdEv: netEquityUsdEv,
      usd: numUtil.delimit(numUtil.floor(netEquityUsdEv, USD_PRECISION)),
      money: numUtil.delimit(numUtil.floor(calcValueBalanceUtils.getBalanceEv(netEquityUsdEv, rate.priceEp, usdValueFactor, rate.priceFactor, usdValueFactor), USD_PRECISION)),
      btcEv: netEquityBtcEv,
      btc: numUtil.delimit(numUtil.floor(netEquityBtcEv, BTC_PRECISION)),
      usdtEv: netEquityUsdtEv,
      usdt: numUtil.delimit(numUtil.floor(netEquityUsdtEv, USD_PRECISION)),
    },
    totalDebt: {
      usdEv: totalDebtUsdEv,
      usd: numUtil.delimit(numUtil.floor(totalDebtUsdEv, USD_PRECISION)),
      money: numUtil.delimit(numUtil.floor(calcValueBalanceUtils.getBalanceEv(totalDebtUsdEv, rate.priceEp, usdValueFactor, rate.priceFactor, usdValueFactor), USD_PRECISION)),
      btcEv: totalDebtBtcEv,
      btc: numUtil.delimit(numUtil.floor(totalDebtBtcEv, BTC_PRECISION)),
      usdtEv: totalDebtUsdtEv,
      usdt: numUtil.delimit(numUtil.floor(totalDebtUsdtEv, USD_PRECISION)),
    },
    totalMargin: {
      usdEv: totalMarginUsdEv,
      usd: numUtil.delimit(numUtil.floor(totalMarginUsdEv, USD_PRECISION)),
      money: numUtil.delimit(numUtil.floor(calcValueBalanceUtils.getBalanceEv(totalMarginUsdEv, rate.priceEp, usdValueFactor, rate.priceFactor, usdValueFactor), USD_PRECISION)),
      btcEv: totalMarginBtcEv,
      btc: numUtil.delimit(numUtil.floor(totalMarginBtcEv, BTC_PRECISION)),
      usdtEv: totalMarginUsdtEv,
      usdt: numUtil.delimit(numUtil.floor(totalMarginUsdtEv, USD_PRECISION)),
    },
  };
}

function initAssets() {
  return {
    isInit: true,
    btcEv: 0,
    btc: (0).toFixed(BTC_PRECISION),
    usdEv: 0,
    usd: (0).toFixed(USD_PRECISION),
    money: (0).toFixed(USD_PRECISION),
    usdtEv: 0,
    usdt: (0).toFixed(USD_PRECISION),
  };
}

function createInitMarginAssetsOverview(): MarginAssetsOverview {
  return {
    totalEquityEv: '0',
    totalDebtEv: '0',
    netEquityEv: '0',
    totalEquityUsd: '--',
    totalDebtUsd: '--',
    netEquityUsd: '--',
    totalEquityBtc: '--',
    totalDebtBtc: '--',
    netEquityBtc: '--',
    maxTransferOutValueEv: '0',
    riskLevel: '--',
    riskLevelEr: 'Infinity',
    totalAvailableEv: '0',
    totalAvailableUsd: '--',
    totalMarginEv: '0',
    totalMarginUsd: '--',
    totalFreezeEv: '0',
    totalFreezeUsd: '--',
  };
}
